import React from "react";
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import FacilityNetworkBackButton from "./FacilityNetworkBackButton";
import FacilityNetworkFigmaCard from "./FacilityNetworkFigmaCard";
import {
  SNF_DASHBOARD_SUBTITLE,
  SNF_ROSTER_SUBTITLE,
  SNF_TYPE_CODE,
  buildLiveSnfDashboardModel,
  collectLinkedSnfFacilities,
  snfToneColor,
  type SnfKpiTile,
  type SnfRosterRow,
  type SnfTone,
} from "./facilityNetworkSnfDashboardUtils";
import {
  getNetworkSnf,
  getNetworkSnfForNetwork,
  type NetworkDashboardMeta,
  type SnfDashboardData,
} from "@services/networkDashboard";
import { FN } from "./facilityNetworkTheme";

type Props = {
  members: FacilityHierarchyInput[];
  selectedFacilityId: number | null;
  onBack: () => void;
  onOpenFacility?: (facility: MyFacilityListRow) => void;
  onOpenNetworkFacility?: (ccn: string) => void;
  networkId?: number | null;
};

const STATUS_NEUTRAL = "#605e5c";
const VALUE_NEUTRAL = "#323130";
const STAR_FILL = "#F2C94C";
const STAR_EMPTY = "#C8C6C4";

function KpiTile({ tile }: { tile: SnfKpiTile }) {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: { xs: "100%", sm: "calc(50% - 4px)", md: 0 },
        bgcolor: "#fff",
        border: "1px solid #e5e5e5",
        borderRadius: "4px",
        boxShadow:
          "1px 1px 2px rgba(98,102,104,0.05), -1px -1px 2px rgba(98,102,104,0.05)",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 14,
          fontWeight: 600,
          color: STATUS_NEUTRAL,
          lineHeight: "20px",
        }}
      >
        {tile.label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 24,
          fontWeight: 600,
          color: tile.valueTone ? snfToneColor(tile.valueTone) : VALUE_NEUTRAL,
          letterSpacing: FN.figmaTitleTracking,
          lineHeight: "32px",
        }}
      >
        {tile.value}
      </Typography>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 12,
          color: STATUS_NEUTRAL,
          letterSpacing: "0.03px",
          lineHeight: "16px",
        }}
      >
        {tile.sublabel}
      </Typography>
    </Box>
  );
}

function StarRating({ value }: { value: number | null }) {
  if (value == null) {
    return <Typography sx={{ fontSize: 12, color: STATUS_NEUTRAL }}>—</Typography>;
  }
  const icons = [];
  for (let i = 1; i <= 5; i += 1) {
    if (value >= i) {
      icons.push(
        <StarIcon key={i} sx={{ fontSize: 12, color: STAR_FILL }} />,
      );
    } else if (value >= i - 0.5) {
      icons.push(
        <StarHalfIcon key={i} sx={{ fontSize: 12, color: STAR_FILL }} />,
      );
    } else {
      icons.push(
        <StarBorderIcon key={i} sx={{ fontSize: 12, color: STAR_EMPTY }} />,
      );
    }
  }
  return <Box sx={{ display: "inline-flex", alignItems: "center", gap: "1px" }}>{icons}</Box>;
}

function ToneText({ label, tone }: { label: string; tone: SnfTone }) {
  return (
    <Typography
      sx={{
        fontFamily: FN.fontFamily,
        fontSize: 12,
        color: snfToneColor(tone),
        letterSpacing: "0.03px",
        lineHeight: "16px",
      }}
    >
      {label}
    </Typography>
  );
}

function IjBadge({ count }: { count: number }) {
  if (count <= 0) {
    return (
      <Typography sx={{ fontSize: 12, color: "#737373", letterSpacing: "0.03px" }}>-</Typography>
    );
  }
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: "9px",
        py: "3px",
        borderRadius: "68px",
        bgcolor: "#fde7e9",
        border: "1px solid #e7a3a7",
      }}
    >
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 12,
          color: "#a4262c",
          letterSpacing: "0.03px",
          lineHeight: "16px",
        }}
      >
        {count} IJ
      </Typography>
    </Box>
  );
}

function RosterTable({
  rows,
  onOpenFacility,
  onOpenNetworkFacility,
}: {
  rows: SnfRosterRow[];
  onOpenFacility?: (facility: MyFacilityListRow) => void;
  onOpenNetworkFacility?: (ccn: string) => void;
}) {
  const headerSx = {
    fontFamily: FN.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    color: STATUS_NEUTRAL,
    bgcolor: "#fafafa",
    borderColor: "#e5e5e5",
    py: 1,
    px: 1.5,
  };
  const cellSx = {
    fontFamily: FN.fontFamily,
    borderColor: "#e5e5e5",
    py: 1.5,
    px: 1.5,
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 980 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerSx, width: 270 }}>Facility</TableCell>
            <TableCell sx={headerSx}>Stars</TableCell>
            <TableCell sx={headerSx}>HPRD</TableCell>
            <TableCell sx={headerSx}>F-Tags Active</TableCell>
            <TableCell sx={headerSx}>IJ Flags</TableCell>
            <TableCell sx={headerSx}>Next Survey</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.key}
              hover
              onClick={() => {
                if (row.dashboardFacility && onOpenFacility) {
                  onOpenFacility(row.dashboardFacility);
                } else if (row.facilityId == null && onOpenNetworkFacility) {
                  const ccn = row.metaLine.match(/^CCN ([^ ·]+)/)?.[1];
                  if (ccn) onOpenNetworkFacility(ccn);
                }
              }}
              sx={{
                cursor: row.dashboardFacility || onOpenNetworkFacility ? "pointer" : "default",
                "&:hover": { bgcolor: "#f3fdff" },
              }}
            >
              <TableCell sx={cellSx}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: VALUE_NEUTRAL }}>
                  {row.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: STATUS_NEUTRAL }}>{row.metaLine}</Typography>
                {row.missingSourcesLabel ? (
                  <Typography sx={{ fontSize: 11, color: "#8b4409" }}>
                    {row.missingSourcesLabel}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell sx={cellSx}>
                <StarRating value={row.stars} />
              </TableCell>
              <TableCell sx={cellSx}>
                <ToneText label={row.hprdLabel} tone={row.hprdTone} />
              </TableCell>
              <TableCell sx={cellSx}>
                <Typography sx={{ fontSize: 12, color: "#737373" }}>{row.fTagsActive ?? "—"}</Typography>
              </TableCell>
              <TableCell sx={cellSx}>
                <IjBadge count={row.ijCount ?? 0} />
              </TableCell>
              <TableCell sx={cellSx}>
                <ToneText label={row.nextSurveyLabel} tone={row.nextSurveyTone} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

type LiveLoadState =
  | { scopeKey: string; status: "idle" | "loading" }
  | { scopeKey: string; status: "loaded"; data: SnfDashboardData; meta: NetworkDashboardMeta }
  | { scopeKey: string; status: "error"; message: string };

function SnfDashboardBody({
  members,
  selectedFacilityId,
  onBack,
  onOpenFacility,
  onOpenNetworkFacility,
  networkId = null,
}: Props) {
  const scopeKey =
    networkId != null
      ? `network:${networkId}`
      : selectedFacilityId != null
        ? `facility:${selectedFacilityId}`
        : "none";
  const linked = collectLinkedSnfFacilities(members, selectedFacilityId);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [loadState, setLoadState] = React.useState<LiveLoadState>({
    scopeKey,
    status: "idle",
  });

  React.useEffect(() => {
    if (scopeKey === "none") {
      setLoadState({ scopeKey, status: "idle" });
      return;
    }
    let disposed = false;
    setLoadState({ scopeKey, status: "loading" });
    const request =
      networkId != null
        ? getNetworkSnfForNetwork(networkId, { limit: 500, offset: 0 })
        : getNetworkSnf(selectedFacilityId!, { limit: 500, offset: 0 });
    void request
      .then((response) => {
        if (!disposed) setLoadState({ scopeKey, status: "loaded", ...response });
      })
      .catch((error) => {
        if (!disposed) {
          setLoadState({
            scopeKey,
            status: "error",
            message: error instanceof Error ? error.message : "Failed to load SNF dashboard.",
          });
        }
      });
    return () => {
      disposed = true;
    };
  }, [networkId, reloadKey, scopeKey, selectedFacilityId]);

  const currentState =
    loadState.scopeKey === scopeKey
      ? loadState
      : ({ scopeKey, status: "loading" } as const);
  const model =
    currentState.status === "loaded"
      ? buildLiveSnfDashboardModel(currentState.data, linked, currentState.meta)
      : null;
  const qualityWarnings =
    currentState.status === "loaded"
      ? (currentState.meta.data_quality_warnings ?? []).filter(Boolean)
      : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        width: "100%",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      <FacilityNetworkBackButton
        label="Back to Networks Overview"
        onClick={onBack}
        variant="link"
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: FN.figmaTitleSize,
              fontWeight: 600,
              color: FN.title,
              letterSpacing: FN.figmaTitleTracking,
              lineHeight: "32px",
            }}
          >
            Skilled Nursing Facilities
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: "24px",
              bgcolor: FN.badgeBg,
              boxShadow: "0px 2px 2px #e9f3f5",
            }}
          >
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.title,
              }}
            >
              {model == null ? "Loading…" : `${model.facilityCount} Facilit${model.facilityCount === 1 ? "y" : "ies"}`}
            </Typography>
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 12,
            color: STATUS_NEUTRAL,
            letterSpacing: "0.03px",
          }}
        >
          {SNF_DASHBOARD_SUBTITLE}
        </Typography>
      </Box>

      {scopeKey === "none" ? (
        <FacilityNetworkFigmaCard>
          <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL }}>
            Select a facility or network to load SNF dashboard data.
          </Typography>
        </FacilityNetworkFigmaCard>
      ) : currentState.status === "loading" || currentState.status === "idle" ? (
        <FacilityNetworkFigmaCard>
          <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL }}>
            Loading SNF dashboard…
          </Typography>
        </FacilityNetworkFigmaCard>
      ) : currentState.status === "error" ? (
        <Box>
          <Typography component="div" sx={{ color: "#a4262c", fontSize: 14 }}>
            {currentState.message}
          </Typography>
          <Button size="small" onClick={() => setReloadKey((key) => key + 1)}>
            Retry
          </Button>
        </Box>
      ) : model && model.facilityCount === 0 && model.roster.length === 0 ? (
        <FacilityNetworkFigmaCard>
          <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL }}>
            No {SNF_TYPE_CODE}s found in this portfolio.
          </Typography>
        </FacilityNetworkFigmaCard>
      ) : model ? (
        <>
          {qualityWarnings.length > 0 ? (
            <Alert severity="warning" icon={false}>
              <Box>
                {qualityWarnings.map((warning, index) => (
                  <Typography
                    key={`${index}-${warning}`}
                    component="div"
                    sx={{ fontSize: 12 }}
                  >
                    {warning}
                  </Typography>
                ))}
              </Box>
            </Alert>
          ) : null}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: "100%" }}>
            {model.kpis.map((tile) => (
              <KpiTile key={tile.id} tile={tile} />
            ))}
          </Box>

          <FacilityNetworkFigmaCard padding={0} gap={0}>
            <Box sx={{ p: 2, borderBottom: "1px solid #e5e5e5" }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  color: STATUS_NEUTRAL,
                }}
              >
                Facility roster
              </Typography>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: STATUS_NEUTRAL,
                  mt: 0.5,
                }}
              >
                {SNF_ROSTER_SUBTITLE}
              </Typography>
            </Box>
            <RosterTable
              rows={model.roster}
              onOpenFacility={onOpenFacility}
              onOpenNetworkFacility={onOpenNetworkFacility}
            />
          </FacilityNetworkFigmaCard>
        </>
      ) : null}
    </Box>
  );
}

class SnfDashboardErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { message: string | null }
> {
  state = { message: null as string | null };

  static getDerivedStateFromError(error: unknown) {
    return {
      message:
        error instanceof Error ? error.message : "Failed to render SNF dashboard.",
    };
  }

  render() {
    if (this.state.message) {
      return (
        <Box sx={{ p: 2, color: "#a4262c", fontSize: 14 }}>{this.state.message}</Box>
      );
    }
    return this.props.children;
  }
}

export default function FacilityNetworkSnfDashboardView(props: Props) {
  return (
    <SnfDashboardErrorBoundary>
      <SnfDashboardBody {...props} />
    </SnfDashboardErrorBoundary>
  );
}
